import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Clock, MapPin, CheckCircle, AlertCircle, Calendar, User, Wrench, X, FileText } from 'lucide-react';

const TechnicianDashboard = () => {
  const { user, hasPermission } = useAuth();
  const [assignments, setAssignments] = useState([]);
  const [stats, setStats] = useState({
    pending: 0,
    inProgress: 0,
    completed: 0,
    overdue: 0
  });
  const [loading, setLoading] = useState(true);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  useEffect(() => {
    // Load assignments from localStorage or use mock data
    const loadAssignments = () => {
      const savedAssignments = localStorage.getItem('technicianAssignments');
      
      if (savedAssignments) {
        return JSON.parse(savedAssignments);
      }
      
      // Default mock assignments if none saved
      return [
        {
          id: 1,
          title: 'HVAC System Maintenance',
          property: 'Building A - Floor 3',
          priority: 'High',
          status: 'Pending',
          dueDate: '2024-01-15',
          estimatedTime: '2 hours',
          description: 'Regular maintenance check for HVAC system'
        },
        {
          id: 2,
          title: 'Plumbing Repair',
          property: 'Building B - Unit 205',
          priority: 'Medium',
          status: 'In Progress',
          dueDate: '2024-01-14',
          estimatedTime: '1.5 hours',
          description: 'Fix leaking faucet in kitchen'
        },
        {
          id: 3,
          title: 'Electrical Inspection',
          property: 'Building C - Common Area',
          priority: 'Low',
          status: 'Completed',
          dueDate: '2024-01-12',
          estimatedTime: '3 hours',
          description: 'Monthly electrical safety inspection'
        }
      ];
    };

    setTimeout(() => {
      const loadedAssignments = loadAssignments();
      setAssignments(loadedAssignments);
      
      // Calculate stats from loaded assignments
      setStats({
        pending: loadedAssignments.filter(a => a.status === 'Pending').length,
        inProgress: loadedAssignments.filter(a => a.status === 'In Progress').length,
        completed: loadedAssignments.filter(a => a.status === 'Completed').length,
        overdue: loadedAssignments.filter(a => new Date(a.dueDate) < new Date() && a.status !== 'Completed').length
      });
      setLoading(false);
    }, 1000);
  }, []);

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'High': return 'text-red-600 bg-red-100';
      case 'Medium': return 'text-yellow-600 bg-yellow-100';
      case 'Low': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Pending': return 'text-orange-600 bg-orange-100';
      case 'In Progress': return 'text-blue-600 bg-blue-100';
      case 'Completed': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const updateAssignmentStatus = (id, newStatus) => {
    setAssignments(prev => {
      const updatedAssignments = prev.map(assignment => 
        assignment.id === id ? { ...assignment, status: newStatus } : assignment
      );
      
      // Save updated assignments to localStorage
      localStorage.setItem('technicianAssignments', JSON.stringify(updatedAssignments));
      
      // Update stats based on new assignment statuses
      setStats({
        pending: updatedAssignments.filter(a => a.status === 'Pending').length,
        inProgress: updatedAssignments.filter(a => a.status === 'In Progress').length,
        completed: updatedAssignments.filter(a => a.status === 'Completed').length,
        overdue: updatedAssignments.filter(a => new Date(a.dueDate) < new Date() && a.status !== 'Completed').length
      });
      
      return updatedAssignments;
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Welcome back, {user?.name || 'Technician'}
        </h1>
        <p className="text-gray-600">
          Here's your work overview for today
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Clock className="h-6 w-6 text-orange-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Pending</p>
              <p className="text-2xl font-bold text-gray-900">{stats.pending}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Wrench className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">In Progress</p>
              <p className="text-2xl font-bold text-gray-900">{stats.inProgress}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Completed</p>
              <p className="text-2xl font-bold text-gray-900">{stats.completed}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 rounded-lg">
              <AlertCircle className="h-6 w-6 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Overdue</p>
              <p className="text-2xl font-bold text-gray-900">{stats.overdue}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Assignments List */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Current Assignments</h2>
        </div>
        <div className="divide-y divide-gray-200">
          {assignments.map((assignment) => (
            <div key={assignment.id} className="p-6 hover:bg-gray-50">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-medium text-gray-900">
                      {assignment.title}
                    </h3>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(assignment.priority)}`}>
                      {assignment.priority}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(assignment.status)}`}>
                      {assignment.status}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                    <div className="flex items-center gap-1">
                      <MapPin className="h-4 w-4" />
                      {assignment.property}
                    </div>
                    <div className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      Due: {assignment.dueDate}
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      Est: {assignment.estimatedTime}
                    </div>
                  </div>
                  
                  <p className="text-gray-700 mb-4">{assignment.description}</p>
                  
                  {assignment.status !== 'Completed' && (
                    <div className="flex gap-2">
                      {assignment.status === 'Pending' && (
                        <button
                          onClick={() => updateAssignmentStatus(assignment.id, 'In Progress')}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          Start Work
                        </button>
                      )}
                      {assignment.status === 'In Progress' && (
                        <button
                          onClick={() => updateAssignmentStatus(assignment.id, 'Completed')}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                        >
                          Mark Complete
                        </button>
                      )}
                      <button 
                        onClick={() => {
                          setSelectedAssignment(assignment);
                          setShowDetailsModal(true);
                        }}
                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        View Details
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Assignment Details Modal */}
      {showDetailsModal && selectedAssignment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
              <h2 className="text-2xl font-bold text-gray-900">Assignment Details</h2>
              <button
                onClick={() => {
                  setSelectedAssignment(null);
                  setShowDetailsModal(false);
                }}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X size={24} className="text-gray-500" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-6">
              {/* Assignment Overview */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-500">Title</p>
                  <h3 className="text-lg font-semibold text-gray-900">{selectedAssignment.title}</h3>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Property</p>
                  <p className="text-gray-800">{selectedAssignment.property}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Due Date</p>
                  <p className="text-gray-800">{selectedAssignment.dueDate}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Estimated Time</p>
                  <p className="text-gray-800">{selectedAssignment.estimatedTime}</p>
                </div>
              </div>

              {/* Status & Priority Badges */}
              <div className="flex flex-wrap gap-2">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getPriorityColor(selectedAssignment.priority)}`}>
                  Priority: {selectedAssignment.priority}
                </span>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedAssignment.status)}`}>
                  Status: {selectedAssignment.status}
                </span>
              </div>

              {/* Description */}
              <div>
                <p className="text-sm font-medium text-gray-500">Description</p>
                <p className="text-gray-800 whitespace-pre-wrap">{selectedAssignment.description}</p>
              </div>

              {/* Additional Details */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
                  <Wrench className="w-5 h-5 mr-2 text-blue-600" />
                  Work Instructions
                </h4>
                <div className="space-y-2 text-sm text-gray-700">
                  <p>• Follow standard safety protocols</p>
                  <p>• Document any issues or complications</p>
                  <p>• Take before and after photos if applicable</p>
                  <p>• Update status upon completion</p>
                </div>
              </div>

              {/* Contact Information */}
              <div className="bg-blue-50 rounded-lg p-4">
                <h4 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
                  <User className="w-5 h-5 mr-2 text-blue-600" />
                  Contact Information
                </h4>
                <div className="space-y-1 text-sm text-gray-700">
                  <p><strong>Property Manager:</strong> John Smith</p>
                  <p><strong>Phone:</strong> +27 11 123 4567</p>
                  <p><strong>Email:</strong> john.smith@property.com</p>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex justify-between p-6 border-t border-gray-200">
              <div className="flex gap-2">
                {selectedAssignment.status === 'Pending' && (
                  <button
                    onClick={() => {
                      updateAssignmentStatus(selectedAssignment.id, 'In Progress');
                      setShowDetailsModal(false);
                      setSelectedAssignment(null);
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Start Work
                  </button>
                )}
                {selectedAssignment.status === 'In Progress' && (
                  <button
                    onClick={() => {
                      updateAssignmentStatus(selectedAssignment.id, 'Completed');
                      setShowDetailsModal(false);
                      setSelectedAssignment(null);
                    }}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    Mark Complete
                  </button>
                )}
              </div>
              <button
                onClick={() => {
                  setSelectedAssignment(null);
                  setShowDetailsModal(false);
                }}
                className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TechnicianDashboard;